/**
 * EngineProgressDisplay
 *
 * Enhanced "command center" display for real-time audit engine progress.
 * Shows AI engines running in parallel with individual status cards,
 * real-time activity messages, and an overall progress tracker.
 *
 * Consumes a progressQueue (from useAsoEngineRun) to avoid React batching issues.
 * The queue is filled synchronously by SSE callbacks before React state updates.
 */

import { useState, useEffect, useRef, useCallback, useId, type ComponentType } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/20/solid';
import {
  SwatchIcon,
  UserGroupIcon,
  DevicePhoneMobileIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  LightBulbIcon,
  SparklesIcon,
  QueueListIcon,
  LanguageIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';
import type { AsoEngineProgress, AsoEngineChildStatus, AsoEngineType } from '@a7y/types';

/** Minimum time (ms) each progress message should be visible to the user */
const MIN_PROGRESS_DISPLAY_MS = 2000;

export interface EngineProgressDisplayProps {
  /** Current progress from useAsoEngineRun (latest value - used as fallback) */
  progress: AsoEngineProgress | null;
  /** Queue of all progress messages from SSE (avoids React batching) */
  progressQueue?: AsoEngineProgress[];
  /** Version counter - changes when queue is updated (triggers re-render) */
  queueVersion?: number;
  /** Whether the engine is currently loading/running */
  isLoading: boolean;
  /** Title to display above the progress indicator */
  title?: string;
  /** Whether to show the spinner icon */
  showSpinner?: boolean;
  /** Additional CSS classes for the container */
  className?: string;
  /** Variant: 'full' for full-page display, 'inline' for inline display */
  variant?: 'full' | 'inline';
  /** Child engine statuses (for audit engine sub-engine progress) */
  childEngines?: AsoEngineChildStatus[];
  /** Audit activity/instance ID — shown at the bottom right with a copy button */
  auditId?: string;
  /** Footer label override when the identifier is not an audit document ID. */
  auditIdLabel?: string;
}

interface QueuedMessage {
  message: string;
  percent: number;
}

// ---------------------------------------------------------------------------
// Engine metadata — rich descriptions, icons, and accent colors
// ---------------------------------------------------------------------------

interface EngineMetadata {
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  accentBorder: string;
  accentText: string;
  accentBg: string;
  accentGlow: string;
}

const ENGINE_META: Record<string, EngineMetadata> = {
  reviews_backfill: {
    label: 'Reviews backfill',
    description: 'Historical review collection',
    icon: ArchiveBoxIcon,
    accentBorder: 'border-l-slate-400',
    accentText: 'text-slate-400',
    accentBg: 'bg-slate-500/10',
    accentGlow: 'shadow-slate-500/20',
  },
  icon: {
    label: 'Icon',
    description: 'AI visual quality assessment',
    icon: SwatchIcon,
    accentBorder: 'border-l-violet-500',
    accentText: 'text-violet-400',
    accentBg: 'bg-violet-500/10',
    accentGlow: 'shadow-violet-500/20',
  },
  competitor: {
    label: 'Competitors',
    description: 'Competitive intelligence scan',
    icon: UserGroupIcon,
    accentBorder: 'border-l-amber-500',
    accentText: 'text-amber-400',
    accentBg: 'bg-amber-500/10',
    accentGlow: 'shadow-amber-500/20',
  },
  screenshot: {
    label: 'Screenshots',
    description: 'Per-screen layout analysis',
    icon: DevicePhoneMobileIcon,
    accentBorder: 'border-l-cyan-500',
    accentText: 'text-cyan-400',
    accentBg: 'bg-cyan-500/10',
    accentGlow: 'shadow-cyan-500/20',
  },
  sentiment: {
    label: 'Reviews & Sentiment',
    description: 'User review intelligence',
    icon: ChatBubbleLeftRightIcon,
    accentBorder: 'border-l-rose-500',
    accentText: 'text-rose-400',
    accentBg: 'bg-rose-500/10',
    accentGlow: 'shadow-rose-500/20',
  },
  storetext: {
    label: 'Store Text',
    description: 'Metadata optimization analysis',
    icon: DocumentTextIcon,
    accentBorder: 'border-l-emerald-500',
    accentText: 'text-emerald-400',
    accentBg: 'bg-emerald-500/10',
    accentGlow: 'shadow-emerald-500/20',
  },
  keyword: {
    label: 'Keywords',
    description: 'Market positioning strategy',
    icon: MagnifyingGlassIcon,
    accentBorder: 'border-l-indigo-400',
    accentText: 'text-indigo-400',
    accentBg: 'bg-indigo-500/10',
    accentGlow: 'shadow-indigo-500/20',
  },
  intent: {
    label: 'Intent Mapping',
    description: 'User intent coverage analysis',
    icon: LightBulbIcon,
    accentBorder: 'border-l-violet-400',
    accentText: 'text-purple-400',
    accentBg: 'bg-violet-500/10',
    accentGlow: 'shadow-violet-500/20',
  },
  aiDiscovery: {
    label: 'AI Discoverability',
    description: 'LLM visibility assessment',
    icon: SparklesIcon,
    accentBorder: 'border-l-indigo-500',
    accentText: 'text-blue-400',
    accentBg: 'bg-indigo-500/10',
    accentGlow: 'shadow-indigo-500/20',
  },
  searchTerm: {
    label: 'Search Terms',
    description: 'Search term prediction',
    icon: QueueListIcon,
    accentBorder: 'border-l-teal-500',
    accentText: 'text-teal-400',
    accentBg: 'bg-teal-500/10',
    accentGlow: 'shadow-teal-500/20',
  },
  l11nAnalysis: {
    label: 'Localization',
    description: 'Locale coverage analysis',
    icon: LanguageIcon,
    accentBorder: 'border-l-sky-500',
    accentText: 'text-sky-400',
    accentBg: 'bg-sky-500/10',
    accentGlow: 'shadow-sky-500/20',
  },
};

/** Canonical engine order */
const ENGINE_ORDER = [
  'reviews_backfill',
  'icon',
  'competitor',
  'screenshot',
  'sentiment',
  'storetext',
  'keyword',
  'intent',
  'aiDiscovery',
  'searchTerm',
  'l11nAnalysis',
];

/**
 * A7Y-5397: the engines of an audit run in display order, without the
 * `reviews_backfill` helper run — the list the /start run view shows, in the
 * same order and with the same names as this command-center view.
 */
export const AUDIT_ENGINE_ORDER: readonly AsoEngineType[] = ENGINE_ORDER.filter(
  (type): type is AsoEngineType => type !== 'reviews_backfill'
);

/** A7Y-5397: an engine's display name, as this view labels its card. */
export function engineProgressLabel(engineType: string): string | null {
  return ENGINE_META[engineType]?.label ?? null;
}

/** Engine types that are data-preparation steps, not AI analysis engines. */
const DATA_PREP_ENGINE_TYPES = new Set<string>(['reviews_backfill']);

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Status icon for a child engine */
function EngineStatusIcon({
  status,
  className = 'size-5',
}: {
  status: string;
  className?: string;
}) {
  if (status === 'running' || status === 'started') {
    return <ArrowPathIcon className={`${className} text-indigo-400 animate-spin`} />;
  }
  if (status === 'success') {
    return <CheckCircleIcon className={`${className} text-emerald-400`} />;
  }
  if (status === 'failure') {
    return <XCircleIcon className={`${className} text-red-400`} />;
  }
  if (status === 'skipped') {
    return <MinusCircleIcon className={`${className} text-zinc-500`} />;
  }
  // pending
  return (
    <span className={`${className} flex items-center justify-center`}>
      <span className="size-1.5 rounded-full bg-gray-300 dark:bg-zinc-600" />
    </span>
  );
}

/**
 * Segmented completion dots: one dot per analysis engine child, in canonical
 * order, so the dots and the "N of M engines complete" line count the same set.
 */
function CompletionDots({ children: engines }: { children: AsoEngineChildStatus[] }) {
  return (
    <div className="flex items-center gap-1">
      {engines.map((child) => {
        const isComplete = child.status === 'success';
        const isFailed = child.status === 'failure';
        const isRunning = child.status === 'running' || child.status === 'started';
        return (
          <span
            key={child.engineType}
            data-testid="engine-completion-dot"
            data-status={child.status}
            className={`size-2.5 rounded-full transition-all duration-500 ${
              isComplete
                ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]'
                : isFailed
                  ? 'bg-red-400'
                  : isRunning
                    ? 'bg-indigo-400 animate-pulse'
                    : 'bg-gray-300 dark:bg-zinc-700'
            }`}
          />
        );
      })}
    </div>
  );
}

/** Single engine card (full variant) */
function EngineCard({
  child,
  compact,
  fullWidth,
}: {
  child: AsoEngineChildStatus;
  compact?: boolean;
  /** Data-preparation steps span the whole grid row (`col-span-full` works for any column count). */
  fullWidth?: boolean;
}) {
  const meta = ENGINE_META[child.engineType];
  if (!meta) return null;

  const isRunning = child.status === 'running' || child.status === 'started';
  const isSuccess = child.status === 'success';
  const isFailure = child.status === 'failure';
  const isSkipped = child.status === 'skipped';
  const isPending = !isRunning && !isSuccess && !isFailure && !isSkipped;
  const progressMsg = child.progress?.message;

  const Icon = meta.icon;
  const py = compact ? 'py-2' : 'py-3';
  const px = compact ? 'px-4' : 'px-5';

  return (
    <motion.div
      data-testid={`child-engine-${child.engineType}`}
      // A7Y-2401 — machine-readable terminal state. Before this, the E2E specs
      // inferred completion from the copy "Analysis complete" or from the
      // status icon's Tailwind color class (`svg.text-emerald-400`), both of
      // which a copy edit or a palette change would silently break.
      data-status={child.status}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`
        relative overflow-hidden rounded-lg border-l-2
        ${meta.accentBorder}
        ${isRunning ? `bg-gray-100 dark:bg-zinc-800/80 shadow-lg ${meta.accentGlow}` : 'bg-gray-50 dark:bg-zinc-800/50'}
        ${isPending ? 'opacity-50' : ''}
        ${py} ${px} transition-all duration-500
        ${fullWidth ? 'col-span-full' : ''}
      `}
    >
      {/* Animated sweep for running state */}
      {isRunning && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent"
          animate={{ x: ['-100%', '200%'] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
        />
      )}

      <div className="relative flex items-start gap-3.5">
        {/* Engine icon */}
        <div className={`shrink-0 rounded-lg ${compact ? 'p-2' : 'p-2.5'} ${meta.accentBg}`}>
          <Icon className={`${compact ? 'size-5' : 'size-6'} ${meta.accentText}`} />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span
              className={`${compact ? 'text-sm' : 'text-base'} font-medium text-gray-700 dark:text-zinc-200`}
            >
              {meta.label}
            </span>
            <EngineStatusIcon status={child.status} className={compact ? 'size-4.5' : 'size-5'} />
          </div>
          <p className={`${compact ? 'text-xs' : 'text-sm'} text-zinc-500 mt-0.5`}>
            {meta.description}
          </p>

          {/* Activity message */}
          <AnimatePresence mode="wait">
            {isRunning && progressMsg && (
              <motion.p
                key={progressMsg}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className={`${compact ? 'text-xs' : 'text-sm'} font-mono ${meta.accentText} mt-2 truncate`}
              >
                {progressMsg}
              </motion.p>
            )}
            {isSuccess && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`${compact ? 'text-xs' : 'text-sm'} text-emerald-500/80 mt-2`}
              >
                Analysis complete
              </motion.p>
            )}
            {isFailure && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`${compact ? 'text-xs' : 'text-sm'} text-red-400/80 mt-2`}
              >
                {/* A7Y-5658: the reason the report gives for the same section, when there is one. */}
                {child.failureDetail ?? 'Engine error'}
              </motion.p>
            )}
            {isSkipped && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`${compact ? 'text-xs' : 'text-sm'} text-gray-400 dark:text-zinc-600 mt-2 italic`}
              >
                Skipped
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}

/** Audit ID display with copy-to-clipboard */
export function AuditIdFooter({
  auditId,
  label = 'Audit ID',
}: {
  auditId: string;
  label?: string;
}) {
  const [copied, setCopied] = useState(false);
  const clipboardLabel = label === 'Run ID' ? 'Audit Run Id' : 'Audit Id';

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(`${clipboardLabel}: ${auditId}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [auditId, clipboardLabel]);

  return (
    <div className="flex items-center justify-end gap-1.5 mt-3">
      <span className="text-[10px] font-medium uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
        {label}
      </span>
      {/*
        A7Y-2401 / A7Y-2405 — stable hook for the run identifier. The E2E run-
        outcome guard reads it while the card is on screen so that, once the
        card unmounts, it can resolve the run's terminal state server-side and
        report WHY a run ended instead of blind-waiting on the report region.
      */}
      <span
        data-testid="engine-progress-audit-id"
        className="text-xs font-mono text-zinc-400 dark:text-zinc-500 select-all"
      >
        {auditId}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        className="p-0.5 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
        title="Copy audit ID"
      >
        {copied ? (
          <ClipboardDocumentCheckIcon className="size-3.5 text-emerald-500" />
        ) : (
          <ClipboardDocumentIcon className="size-3.5 text-zinc-400 dark:text-zinc-500" />
        )}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function EngineProgressDisplay({
  progress,
  progressQueue,
  queueVersion,
  isLoading,
  title = 'Running...',
  showSpinner = true,
  className = '',
  variant = 'full',
  childEngines,
  auditId,
  auditIdLabel,
}: EngineProgressDisplayProps) {
  // Currently displayed message
  const [displayedMessage, setDisplayedMessage] = useState<QueuedMessage | null>(null);

  // Internal queue copy (to avoid mutating the source queue)
  const internalQueueRef = useRef<QueuedMessage[]>([]);
  // Track how many items we've copied from source queue
  const processedCountRef = useRef(0);
  // Track when we last updated the display
  const lastDisplayTimeRef = useRef<number>(0);
  // Timer for scheduling next display update
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Whether we're actively processing
  const isActiveRef = useRef(false);

  // A7Y-2401 — id for the header status message, referenced by the progress
  // bar's `aria-labelledby` so the bar has an accessible name that tracks the
  // live phase text. Declared here (before the `isLoading` early return) so
  // the hook order stays stable across renders.
  const progressMessageId = useId();

  // Copy new items from progressQueue to internal queue
  useEffect(() => {
    if (!isLoading || !progressQueue) return;

    // Copy any new items from source queue
    if (progressQueue.length > processedCountRef.current) {
      for (let i = processedCountRef.current; i < progressQueue.length; i++) {
        const item = progressQueue[i];
        if (item.message) {
          internalQueueRef.current.push({
            message: item.message,
            percent: item.percent,
          });
        }
      }
      processedCountRef.current = progressQueue.length;
    }
  }, [isLoading, progressQueue, queueVersion]);

  // Function to display the next message from the queue
  const displayNext = useCallback(() => {
    if (!isActiveRef.current) return;

    const queue = internalQueueRef.current;
    if (queue.length === 0) {
      // Queue empty, check again soon
      timerRef.current = setTimeout(displayNext, 100);
      return;
    }

    const now = Date.now();
    const elapsed = now - lastDisplayTimeRef.current;

    if (lastDisplayTimeRef.current === 0 || elapsed >= MIN_PROGRESS_DISPLAY_MS) {
      // Display next message
      const next = queue.shift()!;
      setDisplayedMessage(next);
      lastDisplayTimeRef.current = Date.now();
      // Schedule next check
      timerRef.current = setTimeout(displayNext, MIN_PROGRESS_DISPLAY_MS);
    } else {
      // Wait for remaining time
      const delay = MIN_PROGRESS_DISPLAY_MS - elapsed;
      timerRef.current = setTimeout(displayNext, delay);
    }
  }, []);

  // Start/stop processing based on isLoading
  useEffect(() => {
    if (isLoading) {
      isActiveRef.current = true;
      displayNext();
    } else {
      // Reset everything
      isActiveRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      internalQueueRef.current = [];
      processedCountRef.current = 0;
      lastDisplayTimeRef.current = 0;
      setDisplayedMessage(null);
    }

    return () => {
      isActiveRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isLoading, displayNext]);

  // Don't render if not loading
  if (!isLoading) return null;

  // Use displayedMessage, or fallback to progress prop (for components not using queue)
  const currentMessage = displayedMessage?.message ?? progress?.message ?? null;
  const currentPercent = displayedMessage?.percent ?? progress?.percent ?? 0;
  const hasMessage = currentMessage !== null;

  const hasChildren = childEngines && childEngines.length > 0;

  // ---------------------------------------------------------------------------
  // Subheader copy
  //
  // The reviews_backfill child is a data-preparation step, not an AI engine,
  // so the "N AI engines analyzing..." line is honest only when we exclude it
  // from the count. Three relevant states:
  //   (a) backfill running, AI engines still queued
  //   (b) backfill running, AI engines also running (mixed)
  //   (c) backfill terminal (success/skipped/failure), AI engines now running
  // When no backfill child is present at all (e.g. quick mode), fall through
  // to the original "N AI engines analyzing your listing in parallel" copy.
  // ---------------------------------------------------------------------------

  const aiEngineChildren = hasChildren
    ? childEngines.filter((c) => !DATA_PREP_ENGINE_TYPES.has(c.engineType))
    : [];

  // One progress count: the header line and the dots both count the analysis
  // engines only. Data-preparation steps (reviews_backfill) are shown as a
  // card but are not engines, so they are never part of "N of M".
  const orderedAnalysisChildren = ENGINE_ORDER.flatMap((type) =>
    aiEngineChildren.filter((c) => c.engineType === type)
  );
  const completedCount = orderedAnalysisChildren.filter(
    (c) => c.status === 'success' || c.status === 'failure' || c.status === 'skipped'
  ).length;
  const totalCount = orderedAnalysisChildren.length;
  // Cards: data-preparation steps first (full width), then the analysis engines.
  const orderedCards = ENGINE_ORDER.flatMap((type) =>
    (childEngines ?? []).filter((c) => c.engineType === type)
  );
  const backfillChild = hasChildren
    ? childEngines.find((c) => c.engineType === 'reviews_backfill')
    : undefined;
  const backfillRunning =
    backfillChild?.status === 'running' || backfillChild?.status === 'started';
  const backfillTerminal =
    backfillChild?.status === 'success' ||
    backfillChild?.status === 'skipped' ||
    backfillChild?.status === 'failure';
  const anyAiEngineRunning = aiEngineChildren.some(
    (c) => c.status === 'running' || c.status === 'started'
  );

  let subheaderText: string | null = null;
  if (hasChildren) {
    if (backfillChild && aiEngineChildren.length === 0) {
      // Edge: backfill is the only child. Should not happen in normal flow,
      // but keep a sane fallback.
      subheaderText = backfillRunning
        ? 'Backfilling reviews'
        : 'Reviews backfill complete';
    } else if (backfillRunning && !anyAiEngineRunning) {
      // (a) Backfill running, AI engines still queued.
      subheaderText = `Backfilling reviews · ${aiEngineChildren.length} AI engines queued`;
    } else if (backfillRunning && anyAiEngineRunning) {
      // (b) Mixed: backfill still going, AI engines already started.
      subheaderText = `Backfilling reviews · ${aiEngineChildren.length} AI engines analyzing your listing`;
    } else if (backfillTerminal) {
      // (c) Backfill done; AI engines in flight or finishing.
      const backfillNote =
        backfillChild?.status === 'success'
          ? 'reviews backfill complete'
          : backfillChild?.status === 'skipped'
            ? 'reviews backfill skipped'
            : 'reviews backfill failed';
      subheaderText = `${aiEngineChildren.length} AI engines analyzing your listing · ${backfillNote}`;
    } else {
      // No backfill child (legacy / quick mode), or backfill still pending.
      subheaderText = `${aiEngineChildren.length} AI engines analyzing your listing in parallel`;
    }
  }

  // -------------------------------------------------------------------------
  // Engine card grid (shared between variants)
  // -------------------------------------------------------------------------

  const engineGrid = hasChildren ? (
    <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
      {orderedCards.map((child) => (
        <EngineCard
          key={child.instanceId}
          child={child}
          compact={variant === 'inline'}
          fullWidth={DATA_PREP_ENGINE_TYPES.has(child.engineType)}
        />
      ))}
    </div>
  ) : null;

  // -------------------------------------------------------------------------
  // Progress header (shared structure, different sizing)
  // -------------------------------------------------------------------------

  const progressHeader = (
    <div className="space-y-4">
      {/* Phase message + completion count */}
      <div className="flex items-center justify-between gap-3">
        {/*
          A7Y-2401 — `aria-live` and the `aria-labelledby` target both live on
          THIS wrapper, not on the `motion.span` inside it. `AnimatePresence
          mode="wait"` unmounts and remounts that span on every phase message,
          and a live region that is itself replaced is not reliably announced;
          an id on a remounting node also blinks out of the accessibility tree
          mid-transition, which would leave the progress bar transiently
          nameless. The wrapper is stable for the life of the run.
        */}
        <div
          id={progressMessageId}
          aria-live="polite"
          aria-atomic="true"
          className="flex items-center gap-3 min-w-0"
        >
          {showSpinner && (
            <ArrowPathIcon
              className={`${variant === 'inline' ? 'size-5' : 'size-6'} text-indigo-400 animate-spin shrink-0`}
            />
          )}
          <AnimatePresence mode="wait">
            <motion.span
              key={currentMessage ?? 'starting'}
              data-testid="engine-progress-message"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              className={`${variant === 'inline' ? 'text-base' : 'text-lg'} font-medium text-gray-700 dark:text-zinc-200 truncate`}
            >
              {hasMessage ? (
                currentMessage
              ) : (
                <span className="text-zinc-500">Initializing ASO engines...</span>
              )}
            </motion.span>
          </AnimatePresence>
        </div>
        <span
          data-testid="engine-progress-percent"
          className={`${variant === 'inline' ? 'text-sm' : 'text-base'} font-mono text-indigo-400 tabular-nums shrink-0`}
        >
          {Math.round(currentPercent)}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="relative">
        <div
          role="progressbar"
          aria-valuenow={Math.round(currentPercent)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-labelledby={progressMessageId}
          data-testid="engine-progress-bar"
          className="w-full bg-gray-200 dark:bg-zinc-800 rounded-full h-2 overflow-hidden"
        >
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-indigo-600 via-indigo-500 to-indigo-400"
            initial={{ width: 0 }}
            animate={{ width: `${currentPercent}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Completion dots + counter */}
      {aiEngineChildren.length > 0 && (
        <div className="flex items-center justify-between">
          <CompletionDots>{orderedAnalysisChildren}</CompletionDots>
          <span className="text-sm text-zinc-500 tabular-nums">
            {completedCount} of {totalCount} engines complete
          </span>
        </div>
      )}
    </div>
  );

  // -------------------------------------------------------------------------
  // Inline variant
  // -------------------------------------------------------------------------

  if (variant === 'inline') {
    return (
      <div className={`space-y-3 ${className}`}>
        {progressHeader}
        {engineGrid}
        {auditId && <AuditIdFooter auditId={auditId} label={auditIdLabel} />}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Full variant (default) — command center layout
  // -------------------------------------------------------------------------

  return (
    <div className={`flex flex-col items-center justify-center py-10 ${className}`}>
      <div className="w-full max-w-3xl space-y-6">
        {/* Title */}
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-zinc-100 tracking-tight">
            {title}
          </h2>
          {hasChildren && subheaderText && (
            <p className="text-sm text-zinc-500 mt-1.5">{subheaderText}</p>
          )}
        </div>

        {/* Progress header */}
        {progressHeader}

        {/* Engine grid */}
        {engineGrid}

        {auditId && <AuditIdFooter auditId={auditId} label={auditIdLabel} />}
      </div>
    </div>
  );
}
